import {
  Form,
  LoadingSpinner,
  CurecallSelectItems,
  CurecallButton,
  ECurecallButtonAppearance,
  ECurecallSize,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { PatientService } from 'src/services/patient.service'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useNotification } from 'src/providers/notification.provider'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ProjectService } from 'src/services/project.service'
import { useCurrentProject } from 'src/providers/current-project.provider'

type TDocument = {
  id: number
  name: string
}

interface FormValues {
  document: {
    name: string
    id: number
  }
}

interface DocumentFormProps {
  onCancel: () => void
}

const DocumentForm = ({ onCancel }: DocumentFormProps) => {
  const { patientId, projectId } = useParams()
  const { project } = useCurrentProject()

  const {
    isLoading,
    error,
    data: documents,
  } = useQuery<TDocument[]>({
    queryKey: ['project-documents', project.id],
    queryFn: () => ProjectService.getDocuments(project.id, true),
  })
  const methods = useForm<FormValues>()
  const { notify } = useNotification()
  const { t } = useTranslation()

  const items = useMemo<CurecallSelectItems>(() => {
    if (!documents) return []

    return documents?.map((document) => ({
      label: document.name,
      value: {
        name: document.name,
        id: document.id,
      },
    }))
  }, [documents])

  const {
    formState: { isSubmitting },
  } = methods

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    try {
      await PatientService.postDocument(Number(projectId), Number(patientId), values.document.id)
      notify({
        title: t('components.documents.document-sent-title'),
        message: t('components.documents.document-sent-message', { name: values.document.name }),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
    } catch (e) {
      notify({ title: t('common.actions.error'), message: e, appearance: 'ERROR' })
    }
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error) {
    return <ErrorQueryHandler error={error} />
  }

  return (
    <FormProvider {...methods}>
      <form method="POST" action="#" onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="c-max-w-lg">
          <Form.Select
            label={t('components.documents.choose-the-document-to-send')}
            name="document"
            items={items}
            placeholder={t('components.documents.select-a-document')}
          />
        </div>
        <div className="c-flex c-items-center c-gap-5 c-justify-center c-mt-14">
          <CurecallButton
            appearance={ECurecallButtonAppearance.CANCEL}
            size={ECurecallSize.SMALL}
            onClick={onCancel}
          >
            {t('common.actions.cancel')}
          </CurecallButton>
          <CurecallButton
            size={ECurecallSize.SMALL}
            type="submit"
            isLoading={isSubmitting}
            className="c-w-60"
          >
            {t('components.documents.confirm-document-sending')}
          </CurecallButton>
        </div>
      </form>
    </FormProvider>
  )
}

export default DocumentForm
